<script setup lang="ts">
  import { ref, computed, watch } from "vue";
  import { useAccountsStore } from "@stores/accounts";
  import { FormDialog, FormField } from "@/components/episto-ui/form-dialog";
  import { Input } from "@/components/ui/input";
  import { useToastsStore } from "@stores/toastsStore";
  import { getClientId } from "@/utils/clientId";
  import ProspectsField, { type Prospect } from "@/components/Admin/ProspectsField.vue";

  const props = withDefaults(
    defineProps<{
      open: boolean;
      initialName?: string;
      initialProspects?: Prospect[];
      prospectsSource?: string | null;
    }>(),
    {
      initialName: "",
      initialProspects: () => [],
      prospectsSource: null,
    },
  );

  const emit = defineEmits<{
    "update:open": [value: boolean];
    created: [];
  }>();

  const accountsStore = useAccountsStore();
  const toast = useToastsStore();

  const name = ref("");
  const prospects = ref<Prospect[]>([]);
  const loading = ref(false);

  watch(
    () => props.open,
    (open) => {
      if (!open) return;
      name.value = props.initialName;
      prospects.value = props.initialProspects.map((p) => ({ ...p }));
    },
    { immediate: true },
  );

  // The account is always standalone, so nobody inherits access to it: at least
  // one prospect is required or it would be created unreachable.
  const canSave = computed(
    () =>
      name.value.trim().length > 0 &&
      prospects.value.length > 0 &&
      prospects.value.every(
        (p) => p.status === "existing" || (p.first_name.trim() && p.last_name.trim()),
      ),
  );

  const save = async (): Promise<void> => {
    loading.value = true;
    try {
      await accountsStore.createFreemiumAccount({
        name: name.value.trim(),
        prospects: prospects.value.map((p) => ({
          email: p.email,
          first_name: p.first_name,
          last_name: p.last_name,
        })),
        clientId: getClientId(),
      });
      toast.success("Freemium account created");
      emit("created");
      emit("update:open", false);
    } catch (error) {
      console.error(error);
      toast.error("An error occurred");
    } finally {
      loading.value = false;
    }
  };
</script>

<template>
  <FormDialog
    :open="props.open"
    entity-name="Freemium account"
    :loading="loading"
    :can-save="canSave"
    @update:open="emit('update:open', $event)"
    @save="save">
    <FormField
      id="freemium-name"
      label="Account name">
      <Input
        id="freemium-name"
        v-model="name"
        placeholder="Company or team name"
        class="text-sm" />
    </FormField>

    <FormField
      id="prospects"
      label="Prospects">
      <div class="grid gap-3">
        <ProspectsField v-model="prospects" />

        <p
          v-if="props.prospectsSource"
          class="text-muted-foreground text-xs">
          Prefilled with the users of {{ props.prospectsSource }} — they get access to both accounts.
        </p>
      </div>
    </FormField>
  </FormDialog>
</template>
